import type { BpReading } from '../lib/types';

/** Seven days of systolic and diastolic as an inline SVG line chart. Gaps are days without a reading. */
export function BpChart({ trend }: { trend: Array<{ label: string; reading: BpReading | null }> }) {
  const w = 640;
  const h = 260;
  const pad = { l: 38, r: 14, t: 16, b: 30 };
  const values = trend.flatMap((t) => (t.reading ? [t.reading.systolic, t.reading.diastolic] : []));
  const lo = Math.min(60, ...values) - 8;
  const hi = Math.max(150, ...values) + 8;
  const x = (i: number) => pad.l + (i * (w - pad.l - pad.r)) / Math.max(1, trend.length - 1);
  const y = (v: number) => pad.t + ((hi - v) * (h - pad.t - pad.b)) / (hi - lo);
  const ticks = [60, 80, 100, 120, 140].filter((v) => v > lo && v < hi);

  const path = (pick: (r: BpReading) => number): string => {
    let out = '';
    trend.forEach((t, i) => {
      if (!t.reading) return;
      const point = `${x(i).toFixed(1)},${y(pick(t.reading)).toFixed(1)}`;
      out += !out || !trend[i - 1]?.reading ? ` M${point}` : ` L${point}`;
    });
    return out.trim();
  };

  return (
    <svg className="bp-chart" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" role="img" aria-label="Seven-day blood pressure trend">
      {ticks.map((v) => (
        <g key={v}>
          <line className="grid-line" x1={pad.l} x2={w - pad.r} y1={y(v)} y2={y(v)} />
          <text className="axis" x={pad.l - 8} y={y(v) + 4} textAnchor="end">
            {v}
          </text>
        </g>
      ))}
      <path d={path((r) => r.systolic)} className="line-systolic" />
      <path d={path((r) => r.diastolic)} className="line-diastolic" />
      {trend.map((t, i) =>
        t.reading ? (
          <g key={`p${i}`}>
            <circle cx={x(i)} cy={y(t.reading.systolic)} r={3.5} className="dot-systolic" />
            <circle cx={x(i)} cy={y(t.reading.diastolic)} r={3.5} className="dot-diastolic" />
          </g>
        ) : null,
      )}
      {trend.map((t, i) => (
        <text key={`l${i}`} className="axis" x={x(i)} y={h - 8} textAnchor="middle">
          {t.label}
        </text>
      ))}
    </svg>
  );
}
